import { assign, ErrorActorEvent, fromPromise, setup } from 'xstate';
import { sendCustomSuccessAlert, showAxiosErrorAction } from '~/features/actions';
import { extractBackendErrorDetail } from '~/features/utils';
import { CONFIRMATION_VIEW_STATE, type AppActorSystem } from '~/features/xstateSystem';
import { API_CALLS } from './api';
import { CONTRACTS_MAX_PAGE_SIZE, CONTRACTS_PAGE_SIZE } from './constants';
import {
  Context,
  ContractListItem,
  DoneGetDetailEvent,
  DoneGetListEvent,
  Events,
  FilterTab,
  OnSelectEvent,
  Scope,
} from './types';

// Mirrors the server's list filters (routes/contracts/controllers.py).
const tabForContract = (contract: ContractListItem): FilterTab | null => {
  if (contract.status === 'ACTIVE') return 'active';
  if (contract.status === 'COMPLETED' || contract.status === 'TERMINATED') return 'past';
  // [MCV-1907] Every PENDING contract is upcoming: a start date that has arrived
  // does not make an unsigned contract active, and a date cut-off here left it on no tab.
  if (contract.status === 'PENDING') return 'upcoming';
  return null;
};

// A sign or termination can move a linked contract to another tab. Reload it
// through the linked path so the tab follows its new status and the list is
// not seeded with the pre-action detail.
// [MCV-1936] Without a link, reload the rows already loaded in one request
// ($_RELOAD_AFTER_ACTION) so the acted-on contract stays selected even when Load more
// brought it in; a fresh page 1 would drop it.
const reloadAfterAction = <
  const Actions extends readonly (
    | 'sendError'
    | 'sendTerminationRequested'
    | 'sendTerminationWithdrawn'
    | 'setLoading'
  )[],
>(
  actions: Actions
) =>
  [
    {
      guard: ({ context }: { context: Context }) => context.linkedContractId !== null,
      target: '$_LOAD_LINKED_DETAIL',
      actions,
    },
    { target: '$_RELOAD_AFTER_ACTION', actions },
  ] as const;

const RELOAD_AFTER_ACTION = reloadAfterAction(['setLoading']);
const RELOAD_AFTER_TERMINATION_REQUESTED = reloadAfterAction([
  'sendTerminationRequested',
  'setLoading',
]);
const RELOAD_AFTER_TERMINATION_WITHDRAWN = reloadAfterAction([
  'sendTerminationWithdrawn',
  'setLoading',
]);

// A sign, termination request or withdrawal that fails usually means this client's
// copy is stale: a 409 says the contract is no longer pending, this side already
// signed, or a termination request is already pending (or no longer is). Show the
// error, then reload exactly as a success does, so the pane stops offering the
// action.
const RELOAD_AFTER_FAILED_ACTION = reloadAfterAction(['sendError', 'setLoading']);

// [MCV-1829] A sign the server rejects as a bad request (a typed name that is too
// short, too long or has control characters, or no consent) is not a stale pane:
// the contract is unchanged and the dialog is still open, so the detail is shown
// there and nothing reloads. Every other failure (409 already signed or not
// pending, 403, 5xx) still takes the toast-and-reload path above.
const signErrorResponse = (event: unknown) =>
  (event as ErrorActorEvent<{ response?: { status?: number; data?: { detail?: unknown } } }>).error
    ?.response;
const isBadRequestSign = ({ event }: { event: unknown }) =>
  signErrorResponse(event)?.status === 400;

// Clearing a link mid-load (e.g. Back to the bare contracts URL) must exit the
// linked states so a late detail/list result can't reapply the old link. Only
// these states: a row click clears the link after ON_SELECT has already moved
// to $_LOAD_DETAIL, and must not restart the list and cancel that fetch.
const DROP_PENDING_LINK = {
  target: '$_LOAD',
  actions: ['clearContractLink', 'setLoading'],
} as const;

// [MCV-1906] Every (re)load starts again at page 1, whatever page a previous
// Load more had reached.
const FIRST_PAGE_INPUT = ({ context, event }: Scope) => ({
  context: { ...context, page: 1 },
  event,
});

// [MCV-1936] One request for every row loaded so far, capped at what the server accepts.
const reloadPageSize = (context: Context) =>
  Math.min(context.page * CONTRACTS_PAGE_SIZE, CONTRACTS_MAX_PAGE_SIZE);

// [MCV-1936] Past the cap a reload cannot cover everything loaded, so the selected contract
// may have been loaded by a page beyond the rows returned. A full capped reload that does
// not list it is the only case where its absence is ambiguous: it is either further down
// or the action moved it to another tab.
const selectionBeyondReload = (context: Context, rows: ContractListItem[]) =>
  context.selectedContractId !== null &&
  reloadPageSize(context) === CONTRACTS_MAX_PAGE_SIZE &&
  rows.length === CONTRACTS_MAX_PAGE_SIZE &&
  !rows.some((c) => c.id === context.selectedContractId);

export const marketContractsMachine = setup({
  types: {
    context: {} as Context,
    events: {} as Events,
    input: {} as Partial<Context>,
  },
  actors: {
    getContracts: fromPromise(API_CALLS.getList),
    getContractDetail: fromPromise(API_CALLS.getDetail),
    signContract: fromPromise(API_CALLS.sign),
    requestTermination: fromPromise(API_CALLS.requestTermination),
    withdrawTermination: fromPromise(API_CALLS.withdrawTermination),
  },
  actions: {
    setContracts: assign(({ event, context }) => {
      // TODO: XState 5 doesn't narrow event types in setup() actions
      const contracts = (event as unknown as DoneGetListEvent).output;
      // A calendar target can be outside the first page of results. Keep its
      // authorized detail selected instead of falling back to the first row.
      const nextSelectedId =
        context.linkedContractId ??
        (context.selectedContractId && contracts.some((c) => c.id === context.selectedContractId)
          ? context.selectedContractId
          : (contracts[0]?.id ?? null));
      return {
        // [MCV-1906] A fresh list is always page 1; a full page means more may follow.
        page: 1,
        hasMore: contracts.length === CONTRACTS_PAGE_SIZE,
        contracts:
          context.linkedContractId &&
          context.selectedContract &&
          !contracts.some((c) => c.id === context.linkedContractId)
            ? [context.selectedContract, ...contracts]
            : contracts,
        selectedContractId: nextSelectedId,
        selectedContract:
          context.selectedContract?.id === nextSelectedId ? context.selectedContract : null,
      };
    }),
    // [MCV-1936] The rows come from one request sized to the pages already loaded, so
    // the paging position is kept (`page` is what the next Load more follows) rather than
    // reset. The selection survives when it is still listed, and also when a capped
    // reload cannot tell yet whether it merely lies further down: $_RELOAD_CHECK_SELECTED
    // settles that. Otherwise it falls to the first row, as a fresh load does.
    setReloadedContracts: assign(({ event, context }) => {
      const contracts = (event as unknown as DoneGetListEvent).output;
      const pageSize = reloadPageSize(context);
      const keepSelection =
        selectionBeyondReload(context, contracts) ||
        contracts.some((c) => c.id === context.selectedContractId);
      const nextSelectedId = keepSelection
        ? context.selectedContractId
        : (contracts[0]?.id ?? null);
      return {
        contracts,
        page: pageSize / CONTRACTS_PAGE_SIZE,
        hasMore: contracts.length === pageSize,
        selectedContractId: nextSelectedId,
        selectedContract:
          context.selectedContract?.id === nextSelectedId ? context.selectedContract : null,
      };
    }),
    // [MCV-1936] The acted-on contract is still on this tab but beyond the capped reload:
    // pin it at the top, as the linked-contract path pins its contract.
    pinReloadedContract: assign(({ event, context }) => {
      const contract = (event as unknown as DoneGetDetailEvent).output;
      return {
        contracts: [contract, ...context.contracts],
        selectedContract: contract,
        detailError: null,
      };
    }),
    selectFirstContract: assign(({ context }) => ({
      selectedContractId: context.contracts[0]?.id ?? null,
      selectedContract: null,
    })),
    // [MCV-1906] Append the next page. Rows already listed are skipped: the pinned
    // linked contract can sit in a later page too. The selection is left alone, and
    // `hasMore` counts the rows the server returned, not the ones that were new.
    appendContracts: assign(({ event, context }) => {
      const next = (event as unknown as DoneGetListEvent).output;
      const listed = new Set(context.contracts.map((c) => c.id));
      return {
        page: context.page + 1,
        hasMore: next.length === CONTRACTS_PAGE_SIZE,
        contracts: [...context.contracts, ...next.filter((c) => !listed.has(c.id))],
      };
    }),
    setContractDetail: assign({
      detailError: null,
      // TODO: XState 5 doesn't narrow event types in setup() actions
      selectedContract: ({ event }) => (event as unknown as DoneGetDetailEvent).output,
    }),
    setLinkedContractDetail: assign(({ event, context }) => {
      const contract = (event as DoneGetDetailEvent).output;
      return {
        selectedContract: contract,
        contracts: [contract],
        // [MCV-1906] The rows are replaced, so the paging restarts with them. If the
        // list that follows fails, Load more must not fetch the page after the old one.
        page: 1,
        hasMore: false,
        // A contract whose status no tab lists stays in the current tab, pinned
        // there only while the link is active.
        filterTab: tabForContract(contract) ?? context.filterTab,
      };
    }),
    clearContractLink: assign({ linkedContractId: null, detailError: null }),
    setDetailError: assign({
      selectedContract: null,
      detailError:
        'Unable to open this contract. It may no longer be available, or you may not have access. Please try again or select another contract.',
    }),
    clearSignError: assign({ signError: null }),
    setSignError: assign({
      signError: ({ event }) =>
        extractBackendErrorDetail(signErrorResponse(event)?.data?.detail) ??
        'Unable to sign this contract. Check your name and try again.',
    }),
    clearHasMore: assign({ hasMore: false }),
    setLoading: assign({ loading: true }),
    resetLoading: assign({ loading: false }),
    sendError: showAxiosErrorAction,
    // The alert is a courtesy: a failure to show it must not block the reload.
    sendTerminationRequested: ({ system }) => {
      try {
        sendCustomSuccessAlert({
          system: system as AppActorSystem,
          text: 'Termination requested',
          subText: 'Support will review it, and the other party has been notified.',
        });
      } catch {
        return;
      }
    },
    sendTerminationWithdrawn: ({ system }) => {
      try {
        sendCustomSuccessAlert({
          system: system as AppActorSystem,
          text: 'Termination request withdrawn',
          subText: 'The other party has been notified.',
        });
      } catch {
        return;
      }
    },
    openWithdrawTerminationConfirmation: ({ system, self }) => {
      (system as AppActorSystem).get(CONFIRMATION_VIEW_STATE).send({
        type: 'ON_SHOW_CONFIRMATION',
        payload: {
          title: 'Withdraw termination request',
          message:
            'Withdraw the termination request for this contract? Support and the other party will be told it was withdrawn.',
          confirmLabel: 'Withdraw request',
          tone: 'neutral',
          onConfirm: () => self.send({ type: 'ON_CONFIRM_WITHDRAW_TERMINATION' }),
        },
      });
    },
  },
}).createMachine({
  context: () => ({
    contracts: [],
    selectedContractId: null,
    selectedContract: null,
    linkedContractId: null,
    detailError: null,
    signError: null,
    filterTab: 'active' as const,
    roleFilter: null,
    search: '',
    loading: true,
    page: 1,
    hasMore: false,
    employerUser: undefined,
  }),
  initial: '$_VIEW',
  on: {
    ON_LOAD: [
      {
        // The shell sends ON_LOAD independently at boot and on route entry.
        // While a link is active its own load (which doesn't depend on the
        // employer user) already covers the page, so don't restart it.
        guard: ({ context }) => context.linkedContractId !== null,
        actions: assign({ employerUser: ({ event }) => event.payload.employerUser }),
      },
      {
        target: '.$_LOAD',
        actions: [
          assign({ employerUser: ({ event }) => event.payload.employerUser, detailError: null }),
          'setLoading',
        ],
      },
    ],
    ON_OPEN_CONTRACT: {
      target: '.$_LOAD_LINKED_DETAIL',
      actions: [
        assign({
          linkedContractId: ({ event }) => event.payload.id,
          selectedContractId: ({ event }) => event.payload.id,
          selectedContract: null,
          contracts: [],
          roleFilter: null,
          search: '',
          detailError: null,
        }),
        'setLoading',
      ],
    },
    ON_CLEAR_CONTRACT_LINK: { actions: 'clearContractLink' },
    ON_FILTER_TAB: {
      target: '.$_LOAD',
      actions: [
        'clearContractLink',
        assign(({ context, event }) => ({
          filterTab: event.payload.tab,
          // [MCV-1954] The contracts actor lives for the session, so a search or role from an
          // earlier visit would otherwise hide the contract a "Go to Contracts" link points at.
          roleFilter: event.payload.clearFilters ? null : context.roleFilter,
          search: event.payload.clearFilters ? '' : context.search,
        })),
        'setLoading',
      ],
    },
    ON_ROLE_TOGGLE: {
      target: '.$_LOAD',
      actions: [
        'clearContractLink',
        assign({ roleFilter: ({ event }) => event.payload.role }),
        'setLoading',
      ],
    },
    ON_SEARCH: {
      target: '.$_LOAD',
      actions: [
        'clearContractLink',
        assign({ search: ({ event }) => event.payload.search }),
        'setLoading',
      ],
    },
    // [MCV-1906] A row click fetches only the detail, so it does not raise `loading`:
    // that swaps the whole list for a spinner, which reset the scroll position and
    // lost the clicked row once Load more had made the list long.
    ON_SELECT: {
      target: '.$_LOAD_DETAIL',
      actions: [
        'clearContractLink',
        assign({
          // TODO: XState 5 doesn't narrow event types in setup() actions
          selectedContractId: ({ event }) => (event as unknown as OnSelectEvent).payload.id,
          selectedContract: () => null,
        }),
      ],
    },
    ON_REQUEST_TERMINATION: { target: '.$_REQUEST_TERMINATION' },
  },
  states: {
    // [MCV-1727] ON_SIGN is handled here and nowhere else, so a second click (or a
    // click while a detail reload is still running) is ignored instead of posting
    // the signature again. The server answers a repeat with 409 regardless.
    $_VIEW: {
      on: {
        ON_SIGN: { target: '$_SIGN', actions: 'clearSignError' },
        ON_LOAD_MORE: { guard: ({ context }) => context.hasMore, target: '$_LOAD_MORE' },
        // Same for a withdrawal: the confirmation opens only from $_VIEW, and so
        // does its confirm, so a repeat while one is in flight is ignored.
        ON_WITHDRAW_TERMINATION: { actions: 'openWithdrawTerminationConfirmation' },
        ON_CONFIRM_WITHDRAW_TERMINATION: { target: '$_WITHDRAW_TERMINATION' },
      },
    },
    // [MCV-1906] Fetches the next page and appends it. Only reachable from $_VIEW, so
    // it cannot start while any load, detail fetch or action is in flight, and a
    // second click while this one runs is ignored. It does not touch `loading`
    // (the list stays on screen) or the selection. Any event handled at the root
    // (a filter change, a row select) leaves this state and cancels the fetch.
    // The detail pane stays live meanwhile, so its actions are taken here exactly
    // as in $_VIEW. The cancelled fetch never advanced `page`, so the reload that follows
    // an action ($_RELOAD_AFTER_ACTION) covers exactly the pages already loaded.
    $_LOAD_MORE: {
      on: {
        ON_SIGN: { target: '$_SIGN', actions: 'clearSignError' },
        ON_WITHDRAW_TERMINATION: { actions: 'openWithdrawTerminationConfirmation' },
        ON_CONFIRM_WITHDRAW_TERMINATION: { target: '$_WITHDRAW_TERMINATION' },
      },
      invoke: {
        src: 'getContracts',
        input: ({ context, event }: Scope) => ({
          context: { ...context, page: context.page + 1 },
          event,
        }),
        onDone: { target: '$_VIEW', actions: 'appendContracts' },
        onError: { target: '$_VIEW', actions: 'sendError' },
      },
    },
    $_LOAD_LINKED_DETAIL: {
      on: { ON_CLEAR_CONTRACT_LINK: DROP_PENDING_LINK },
      invoke: {
        src: 'getContractDetail',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: { target: '$_LOAD_LINKED_LIST', actions: 'setLinkedContractDetail' },
        // Still load the list so a link the user can't open doesn't leave the
        // page empty; the link keeps the selection off every other contract.
        onError: { target: '$_LOAD_LINKED_LIST', actions: 'setDetailError' },
      },
    },
    $_LOAD_LINKED_LIST: {
      on: { ON_CLEAR_CONTRACT_LINK: DROP_PENDING_LINK },
      invoke: {
        src: 'getContracts',
        input: FIRST_PAGE_INPUT,
        onDone: { target: '$_VIEW', actions: ['setContracts', 'resetLoading'] },
        // The requested detail is still usable if the surrounding list fails.
        onError: { target: '$_VIEW', actions: ['sendError', 'resetLoading'] },
      },
    },
    $_LOAD: {
      invoke: {
        src: 'getContracts',
        input: FIRST_PAGE_INPUT,
        onDone: {
          target: '$_LOAD_FIRST_DETAIL',
          actions: ['setContracts', 'resetLoading'],
        },
        // The old tab's rows are still on screen, so "more" would append the new
        // tab's second page to them.
        onError: {
          target: '$_VIEW',
          actions: ['sendError', 'resetLoading', 'clearHasMore'],
        },
      },
    },
    // [MCV-1936] After a sign, termination request or withdrawal: reload page 1 sized to
    // every page loaded so far, so a contract picked after Load more is still in the rows.
    // Filter, search and role changes, route reloads and the linked path still use $_LOAD.
    // A failed reload leaves the list, page and Load more exactly as they were.
    $_RELOAD_AFTER_ACTION: {
      invoke: {
        src: 'getContracts',
        input: ({ context, event }: Scope) => ({
          context: { ...context, page: 1, pageSize: reloadPageSize(context) },
          event,
        }),
        onDone: [
          {
            guard: ({ context, event }) =>
              selectionBeyondReload(context, (event as unknown as DoneGetListEvent).output),
            target: '$_RELOAD_CHECK_SELECTED',
            actions: 'setReloadedContracts',
          },
          {
            target: '$_LOAD_FIRST_DETAIL',
            actions: ['setReloadedContracts', 'resetLoading'],
          },
        ],
        onError: { target: '$_VIEW', actions: ['sendError', 'resetLoading'] },
      },
    },
    // [MCV-1936] Only after a capped reload that did not list the selected contract: fetch it
    // to learn whether it is still on this tab. If so it is pinned; if the action moved it
    // to another tab the first row is selected, as today (the tab is not switched).
    $_RELOAD_CHECK_SELECTED: {
      invoke: {
        src: 'getContractDetail',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: [
          {
            guard: ({ context, event }) =>
              tabForContract((event as unknown as DoneGetDetailEvent).output) === context.filterTab,
            target: '$_VIEW',
            actions: ['pinReloadedContract', 'resetLoading'],
          },
          { target: '$_LOAD_FIRST_DETAIL', actions: ['selectFirstContract', 'resetLoading'] },
        ],
        onError: {
          target: '$_LOAD_FIRST_DETAIL',
          actions: ['sendError', 'selectFirstContract', 'resetLoading'],
        },
      },
    },
    $_LOAD_FIRST_DETAIL: {
      always: [
        {
          target: '$_LOAD_DETAIL',
          guard: ({ context }) => context.selectedContractId !== null,
        },
        { target: '$_VIEW' },
      ],
    },
    $_LOAD_DETAIL: {
      invoke: {
        src: 'getContractDetail',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: {
          target: '$_VIEW',
          actions: ['setContractDetail', 'resetLoading'],
        },
        onError: {
          target: '$_VIEW',
          actions: ['sendError', 'resetLoading'],
        },
      },
    },
    $_SIGN: {
      invoke: {
        src: 'signContract',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: RELOAD_AFTER_ACTION,
        onError: [
          { guard: isBadRequestSign, target: '$_VIEW', actions: 'setSignError' },
          ...RELOAD_AFTER_FAILED_ACTION,
        ],
      },
    },
    $_REQUEST_TERMINATION: {
      invoke: {
        src: 'requestTermination',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: RELOAD_AFTER_TERMINATION_REQUESTED,
        onError: RELOAD_AFTER_FAILED_ACTION,
      },
    },
    $_WITHDRAW_TERMINATION: {
      invoke: {
        src: 'withdrawTermination',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: RELOAD_AFTER_TERMINATION_WITHDRAWN,
        onError: RELOAD_AFTER_FAILED_ACTION,
      },
    },
  },
});
